<template>
  <div class="game-detail">
    <!-- Loading State -->
    <div v-if="store.loadingDetail" class="gd-loading">
      <div class="gd-loading-thumb"></div>
      <div class="gd-loading-info">
        <div class="gd-loading-title"></div>
        <div class="gd-loading-desc"></div>
        <div class="gd-loading-desc short"></div>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="store.detailError" class="gd-empty">
      <p>{{ store.detailError }}</p>
      <button class="btn-cta" @click="loadGame">{{ $t('common.try_again') }}</button>
    </div>

    <!-- Game Not Found -->
    <div v-else-if="!store.gameDetail" class="gd-empty">
      <p>{{ $t('player.game_detail.not_found') }}</p>
      <button class="btn-cta" @click="router.push({ name: 'player.lobby' })">{{ $t('player.game_detail.back_to_lobby') }}</button>
    </div>

    <!-- Game Detail Content -->
    <div v-else class="gd-content">
      <!-- Back link -->
      <button class="gd-back" @click="router.push({ name: 'player.lobby' })">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="m15 18-6-6 6-6"/>
        </svg>
        {{ $t('player.game_detail.back_to_lobby') }}
      </button>

      <div class="gd-layout">
        <!-- Left Column: Thumbnail + Actions -->
        <div class="gd-left">
          <div
            class="gd-thumb"
            :class="gameAsset ? '' : getGradientClass(store.gameDetail.slug)"
            :style="gameAsset?.bg ? { backgroundImage: `url(${gameAsset.bg})` } : undefined"
          >
            <img
              v-if="gameAsset?.logo"
              :src="gameAsset.logo"
              :alt="store.gameDetail.title"
              class="gd-logo"
            />
            <span v-else class="gd-emoji"><AppIcon :name="getCategoryIcon(store.gameDetail.category)" :size="16" /></span>
            <span
              v-for="badge in store.gameDetail.badges"
              :key="badge"
              class="gd-badge"
              :class="'gd-badge--' + badge.toLowerCase()"
            >
              {{ badge }}
            </span>
          </div>

          <!-- Action buttons -->
          <div class="gd-actions">
            <button class="btn-play" @click="navigateToPlay">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
              {{ $t('player.game_detail.play_now') }}
            </button>
            <div class="gd-action-row">
              <button class="btn-icon-action" :class="{ active: isFavorited }" :title="$t('player.game_detail.add_to_favorites')" @click="toggleFavorite">
                <svg width="18" height="18" viewBox="0 0 24 24" :fill="isFavorited ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
                {{ isFavorited ? $t('player.game_detail.favorited') : $t('player.game_detail.favorite') }}
              </button>
              <button class="btn-icon-action" :title="$t('player.game_detail.verify_fairness')" @click="navigateToVerify">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                {{ $t('player.game_detail.verify_fairness') }}
              </button>
            </div>
          </div>
        </div>

        <!-- Right Column: Info -->
        <div class="gd-right">
          <div class="gd-header">
            <h1 class="gd-title">{{ store.gameDetail.title }}</h1>
            <span class="gd-category">{{ store.gameDetail.category }}</span>
          </div>

          <!-- Stats Row -->
          <div class="gd-stats">
            <div v-if="store.gameDetail.rtp !== null" class="gd-stat">
              <span class="gd-stat-label">{{ $t('player.game_detail.rtp') }}</span>
              <span class="gd-stat-value green">{{ store.gameDetail.rtp }}%</span>
            </div>
            <div class="gd-stat">
              <span class="gd-stat-label">{{ $t('player.game_detail.plays') }}</span>
              <span class="gd-stat-value">{{ formatPlayCount(store.gameDetail.play_count) }}</span>
            </div>
            <div v-if="store.gameDetail.min_bet > 0" class="gd-stat">
              <span class="gd-stat-label">{{ $t('player.game_detail.min_bet') }}</span>
              <span class="gd-stat-value">${{ store.gameDetail.min_bet }}</span>
            </div>
            <div v-if="store.gameDetail.max_bet > 0" class="gd-stat">
              <span class="gd-stat-label">{{ $t('player.game_detail.max_bet') }}</span>
              <span class="gd-stat-value">${{ store.gameDetail.max_bet }}</span>
            </div>
          </div>

          <!-- Description -->
          <div v-if="store.gameDetail.description" class="gd-section">
            <h2 class="gd-section-title">{{ $t('player.game_detail.about') }}</h2>
            <p class="gd-description">{{ store.gameDetail.description }}</p>
          </div>

          <!-- Rules -->
          <div v-if="store.gameDetail.rules_url" class="gd-section">
            <h2 class="gd-section-title">{{ $t('player.game_detail.rules') }}</h2>
            <a :href="store.gameDetail.rules_url" target="_blank" rel="noopener" class="gd-rules-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              {{ $t('player.game_detail.view_rules') }}
            </a>
          </div>

          <!-- Provably Fair Info -->
          <div class="gd-section">
            <div class="gd-fair-tag" @click="navigateToVerify">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              {{ $t('player.game_detail.provably_fair_tag') }}
            </div>
          </div>

          <!-- Recent Wins -->
          <div class="gd-section">
            <h2 class="gd-section-title">{{ $t('player.game_detail.recent_wins') }}</h2>
            <div v-if="store.gameDetail.recent_wins.length === 0" class="gd-no-wins">
              {{ $t('player.game_detail.no_wins') }}
            </div>
            <div v-else class="gd-wins">
              <div class="gd-wins-header">
                <span>{{ $t('player.game_detail.col_player') }}</span>
                <span>{{ $t('player.game_detail.col_bet') }}</span>
                <span>{{ $t('player.game_detail.col_win') }}</span>
                <span>{{ $t('player.game_detail.col_multiplier') }}</span>
              </div>
              <div
                v-for="(win, idx) in store.gameDetail.recent_wins"
                :key="idx"
                class="gd-win-row"
              >
                <span class="gd-win-user">{{ win.username }}</span>
                <span class="gd-win-bet">${{ win.bet_amount.toFixed(2) }}</span>
                <span class="gd-win-amount">${{ win.win_amount.toFixed(2) }}</span>
                <span class="gd-win-mult">{{ win.multiplier.toFixed(2) }}x</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useGamesStore } from '@/stores/games'
import AppIcon from '@/components/AppIcon.vue'
import { updateSeo } from '@/composables/useSeo'

const route = useRoute()
const router = useRouter()
const store = useGamesStore()
const gameAsset = ref<{ bg: string | null; logo: string | null } | null>(null)
const isFavorited = computed(() => {
  if (!store.gameDetail) return false
  return store.isFavorited(store.gameDetail.id)
})

function probeImage(src: string): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(src)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

async function probeGameAssets(slug: string, category: string): Promise<void> {
  const dir = slug.replace(/-/g, '_')
  const assetDir = category === 'slots'
    ? `slots/${dir}`
    : category === 'table'
      ? `table/${dir}`
      : `instant/${dir}`
  const [bg, logo] = await Promise.all([
    probeImage(`/assets/${assetDir}/background.jpg`),
    probeImage(`/assets/${assetDir}/logo.png`),
  ])
  if (bg || logo) {
    gameAsset.value = { bg, logo }
  } else {
    gameAsset.value = null
  }
}

const gradientClasses = [
  'gt1', 'gt2', 'gt3', 'gt4', 'gt5',
  'gt6', 'gt7', 'gt8', 'gt9', 'gt10',
]

function getGradientClass(slug: string): string {
  let hash = 0
  for (let i = 0; i < slug.length; i++) {
    hash = slug.charCodeAt(i) + ((hash << 5) - hash)
  }
  return gradientClasses[Math.abs(hash) % gradientClasses.length]
}

const TABLE_GAMES = ['blackjack', 'baccarat', 'roulette', 'video-poker']
const INSTANT_GAMES = ['dice', 'mines', 'crash', 'plinko', 'keno', 'hilo']

function getGameCategory(gameSlug: string): string {
  if (TABLE_GAMES.includes(gameSlug)) return 'table'
  if (INSTANT_GAMES.includes(gameSlug)) return 'instant'
  return 'slots'
}

function getCategoryIcon(slug: string): string {
  const map: Record<string, string> = {
    slots: 'category-slots',
    table: 'category-table',
    instant: 'category-instant',
    'video-poker': 'category-video-poker',
  }
  return map[slug] ?? 'category-default'
}

function formatPlayCount(count: number): string {
  if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
  if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
  return count.toString()
}

function navigateToPlay() {
  router.push({ name: 'player.game.play', params: { slug: route.params.slug } })
}

function navigateToVerify() {
  router.push('/player/provably-fair')
}

function toggleFavorite() {
  if (store.gameDetail) {
    store.toggleFavorite(store.gameDetail.id)
  }
}

function loadGame() {
  const slug = route.params.slug as string
  if (slug) {
    store.fetchGameDetail(slug)
    probeGameAssets(slug, getGameCategory(slug))
  }
}

onMounted(() => {
  loadGame()
  if (!store.favoritesLoaded) {
    store.fetchFavorites()
  }
})

watch(() => route.params.slug, () => {
  loadGame()
})

// Update SEO when game detail loads
watch(() => store.gameDetail, (game) => {
  if (game) {
    updateSeo(game.title, {
      description: game.description || `Play ${game.title} — provably fair online casino game.`,
    })
  }
})
</script>

<style scoped>
.game-detail {
  padding: var(--space-6, 24px);
  max-width: 960px;
  margin: 0 auto;
  animation: fadeUp 0.3s ease both;
}

@keyframes fadeUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Back button */
.gd-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
  margin-bottom: 20px;
}

.gd-back:hover {
  color: var(--text-primary, #fff);
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.15);
}

/* Layout */
.gd-layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 28px;
}

/* Left Column */
.gd-left {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gd-thumb {
  aspect-ratio: 3 / 4;
  border-radius: var(--radius-xl, 16px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background-size: cover;
  background-position: center;
}

.gd-logo {
  max-width: 80%;
  max-height: 45%;
  object-fit: contain;
  filter: drop-shadow(0 4px 16px rgba(0, 0, 0, 0.6));
  z-index: 1;
}

.gd-emoji {
  font-size: 72px;
  line-height: 1;
}

.gd-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  font-size: 9px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 4px 10px;
  border-radius: var(--radius-sm, 4px);
  z-index: 1;
  background: rgba(0, 231, 1, 0.2);
  color: var(--accent-primary, #00e701);
}

.gd-badge + .gd-badge {
  left: auto;
  right: 10px;
}

.gd-badge--hot {
  background: rgba(237, 66, 69, 0.2);
  color: var(--color-danger, #ed4245);
}

.gd-badge--new {
  background: rgba(20, 117, 225, 0.2);
  color: var(--accent-secondary, #1475e1);
}

.gd-badge--popular {
  background: rgba(255, 215, 0, 0.2);
  color: var(--accent-gold, #ffd700);
}

/* Action buttons */
.gd-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.btn-play {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  letter-spacing: 0.03em;
}

.btn-play:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

.gd-action-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.btn-icon-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 600;
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-icon-action:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.15);
}

.btn-icon-action.active {
  background: rgba(237, 66, 69, 0.1);
  color: var(--color-danger, #ed4245);
  border-color: rgba(237, 66, 69, 0.25);
}

/* Right Column */
.gd-right {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gd-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.gd-title {
  font-family: var(--font-family-display, 'Russo One', sans-serif);
  font-size: clamp(22px, 3vw, 30px);
  line-height: 1.2;
  margin: 0;
}

.gd-category {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

/* Stats */
.gd-stats {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.gd-stat {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: var(--radius-md, 8px);
  padding: 10px 16px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 90px;
}

.gd-stat-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
}

.gd-stat-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.gd-stat-value.green {
  color: var(--color-success, #00e701);
}

/* Sections */
.gd-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gd-section-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.gd-description {
  font-size: 13px;
  line-height: 1.65;
  color: var(--text-secondary, #b1bad3);
  margin: 0;
}

/* Rules link */
.gd-rules-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--accent-secondary, #1475e1);
  text-decoration: none;
  transition: color var(--transition-fast, 150ms ease);
}

.gd-rules-link:hover {
  color: var(--accent-secondary-hover, #1260bf);
}

/* Provably Fair tag */
.gd-fair-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(20, 117, 225, 0.1);
  border: 1px solid rgba(20, 117, 225, 0.2);
  border-radius: var(--radius-full, 9999px);
  padding: 6px 14px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--accent-secondary, #1475e1);
  cursor: pointer;
  transition: background var(--transition-fast, 150ms ease);
  width: fit-content;
}

.gd-fair-tag:hover {
  background: rgba(20, 117, 225, 0.18);
}

/* Recent Wins */
.gd-no-wins {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  padding: 16px;
  text-align: center;
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.gd-wins {
  background: var(--bg-secondary, #1a2c38);
  border-radius: var(--radius-md, 8px);
  border: 1px solid rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.gd-wins-header {
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 8px;
  padding: 10px 14px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.gd-wins-header span:not(:first-child) {
  text-align: right;
}

.gd-win-row {
  display: grid;
  grid-template-columns: 1fr 80px 80px 80px;
  gap: 8px;
  padding: 9px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  align-items: center;
  transition: background var(--transition-fast, 150ms ease);
}

.gd-win-row:last-child {
  border-bottom: none;
}

.gd-win-row:hover {
  background: rgba(255, 255, 255, 0.02);
}

.gd-win-user {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gd-win-bet {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: right;
}

.gd-win-amount {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 11px;
  font-weight: 700;
  color: var(--color-success, #00e701);
  text-align: right;
}

.gd-win-mult {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 11px;
  font-weight: 700;
  color: var(--accent-gold, #ffd700);
  text-align: right;
}

/* Gradient backgrounds (same as lobby) */
.gt1 { background: var(--gradient-gt1, linear-gradient(135deg, #0d1a2b, #0d3a6b)); }
.gt2 { background: var(--gradient-gt2, linear-gradient(135deg, #1a0d1a, #5a0080)); }
.gt3 { background: var(--gradient-gt3, linear-gradient(135deg, #2b0d0d, #8b2020)); }
.gt4 { background: var(--gradient-gt4, linear-gradient(135deg, #0d2b0d, #1a6b1a)); }
.gt5 { background: var(--gradient-gt5, linear-gradient(135deg, #2b2b0d, #8b7a00)); }
.gt6 { background: var(--gradient-gt6, linear-gradient(135deg, #2b1a0d, #8b5a00)); }
.gt7 { background: var(--gradient-gt7, linear-gradient(135deg, #0d2b2b, #00728b)); }
.gt8 { background: var(--gradient-gt8, linear-gradient(135deg, #1f0d2b, #6a0080)); }
.gt9 { background: var(--gradient-gt9, linear-gradient(135deg, #1a1a1a, #4a4a4a)); }
.gt10 { background: var(--gradient-gt10, linear-gradient(135deg, #0d1a1a, #006b6b)); }

/* Loading skeleton */
.gd-loading {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 28px;
}

.gd-loading-thumb {
  aspect-ratio: 3 / 4;
  border-radius: var(--radius-xl, 16px);
  background: linear-gradient(
    90deg,
    var(--bg-secondary, #1a2c38) 25%,
    var(--bg-tertiary, #213743) 50%,
    var(--bg-secondary, #1a2c38) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.gd-loading-info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 8px;
}

.gd-loading-title {
  height: 30px;
  width: 60%;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.gd-loading-desc {
  height: 14px;
  width: 90%;
  border-radius: 4px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.gd-loading-desc.short {
  width: 50%;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

/* Empty/error state */
.gd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 16px;
  gap: 16px;
}

.btn-cta {
  padding: 11px 22px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 700;
  border-radius: var(--radius-md, 8px);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  font-family: inherit;
}

.btn-cta:hover {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: var(--shadow-glow-green, 0 0 20px rgba(0, 231, 1, 0.3));
  transform: translateY(-1px);
}

/* Responsive */
@media (max-width: 768px) {
  .game-detail {
    padding: var(--space-4, 16px);
  }

  .gd-layout,
  .gd-loading {
    grid-template-columns: 1fr;
  }

  .gd-thumb {
    max-width: 240px;
    margin: 0 auto;
  }

  .gd-wins-header,
  .gd-win-row {
    grid-template-columns: 1fr 60px 60px 60px;
    gap: 4px;
    padding: 8px 10px;
  }

  .gd-wins-header {
    font-size: 9px;
  }

  .gd-win-bet,
  .gd-win-amount,
  .gd-win-mult {
    font-size: 10px;
  }
}
</style>
